import { computed, Injectable, signal, TemplateRef } from '@angular/core';
import {
  UI_PAGE_CHROME_DEFAULTS,
  UiPageChrome,
  UiPageChromeConfig,
} from './page-chrome.types';

/**
 * Punto único de comunicación entre las páginas y el App Shell para el
 * marco de página (*page chrome*).
 *
 * Reparto de responsabilidades:
 *
 * | Quién      | Qué hace                                                      |
 * |------------|---------------------------------------------------------------|
 * | Ruta       | Declara el marco estático en `data.chrome`.                    |
 * | App Shell  | Llama a {@link reset} en cada navegación y lee las señales.     |
 * | Página     | Usa {@link patch} solo para datos dinámicos (p. ej. un título   |
 * |            | que incluye el nombre del registro) y proyecta sus plantillas.  |
 *
 * Las páginas nunca leen del servicio: solo escriben. El shell nunca escribe
 * más allá de {@link reset}.
 */
@Injectable({ providedIn: 'root' })
export class UiPageChromeService {
  private readonly _chrome = signal<UiPageChrome>(UI_PAGE_CHROME_DEFAULTS);
  private readonly _titleActions = signal<TemplateRef<unknown> | null>(null);
  private readonly _titleBeforeEnd = signal<TemplateRef<unknown> | null>(null);
  private readonly _titleBarRow = signal<TemplateRef<unknown> | null>(null);
  private readonly _belowTitleBar = signal<TemplateRef<unknown> | null>(null);

  /** Marco vigente completo. */
  readonly chrome = this._chrome.asReadonly();

  readonly variant = computed(() => this._chrome().variant);
  readonly title = computed(() => this._chrome().title);
  readonly titleBarMode = computed(() => this._chrome().titleBarMode);
  readonly showTitleBar = computed(() => this._chrome().showTitleBar);
  readonly showMenubar = computed(() => this._chrome().showMenubar);
  readonly showCompanyFilter = computed(() => this._chrome().showCompanyFilter);
  readonly breadcrumbItems = computed(() => this._chrome().breadcrumbItems);
  readonly breadcrumbHome = computed(() => this._chrome().breadcrumbHome);
  readonly moduleId = computed(() => this._chrome().moduleId);
  readonly activeMenubarId = computed(() => this._chrome().activeMenubarId);

  /** Etiqueta accesible del `<main>`, con el título como respaldo. */
  readonly mainLabel = computed(() => {
    const { mainLabel, title } = this._chrome();
    return mainLabel || title || 'Contenido principal';
  });

  /** Acciones proyectadas en la zona derecha de la barra de título. */
  readonly titleActions = this._titleActions.asReadonly();

  /** Contenido proyectado justo antes de la zona derecha de la barra. */
  readonly titleBeforeEnd = this._titleBeforeEnd.asReadonly();

  /** Filtros de la segunda fila de la barra, solo en modo report. */
  readonly titleBarRow = this._titleBarRow.asReadonly();

  /** La segunda fila se muestra cuando la página registró esa plantilla. */
  readonly showSecondFilterRow = computed(() => this._titleBarRow() !== null);

  /** Contenido proyectado bajo la barra de título (p. ej. pestañas). */
  readonly belowTitleBar = this._belowTitleBar.asReadonly();

  /**
   * Reemplaza el marco completo partiendo de los valores base. Lo llama el
   * shell al terminar cada navegación, con el `chrome` de la ruta activa.
   */
  reset(config?: UiPageChromeConfig): void {
    this._chrome.set({ ...UI_PAGE_CHROME_DEFAULTS, ...config });
  }

  /**
   * Ajusta parte del marco vigente sin tocar el resto. Pensado para valores
   * que solo se conocen en tiempo de ejecución.
   */
  patch(config: UiPageChromeConfig): void {
    this._chrome.update(current => ({ ...current, ...config }));
  }

  registerTitleActions(template: TemplateRef<unknown>): void {
    this._titleActions.set(template);
  }

  registerTitleBeforeEnd(template: TemplateRef<unknown>): void {
    this._titleBeforeEnd.set(template);
  }

  registerTitleBarRow(template: TemplateRef<unknown>): void {
    this._titleBarRow.set(template);
  }

  registerBelowTitleBar(template: TemplateRef<unknown>): void {
    this._belowTitleBar.set(template);
  }

  /**
   * Libera la plantilla solo si sigue siendo la vigente. La guarda importa
   * porque el `router-outlet` puede crear la vista entrante antes de destruir
   * la saliente: sin ella, la saliente borraría el marco de la entrante.
   */
  releaseTitleActions(template: TemplateRef<unknown>): void {
    if (this._titleActions() === template) {
      this._titleActions.set(null);
    }
  }

  /** Contraparte de {@link registerTitleBeforeEnd}, con la misma guarda. */
  releaseTitleBeforeEnd(template: TemplateRef<unknown>): void {
    if (this._titleBeforeEnd() === template) {
      this._titleBeforeEnd.set(null);
    }
  }

  /** Contraparte de {@link registerTitleBarRow}, con la misma guarda. */
  releaseTitleBarRow(template: TemplateRef<unknown>): void {
    if (this._titleBarRow() === template) {
      this._titleBarRow.set(null);
    }
  }

  /** Contraparte de {@link registerBelowTitleBar}, con la misma guarda. */
  releaseBelowTitleBar(template: TemplateRef<unknown>): void {
    if (this._belowTitleBar() === template) {
      this._belowTitleBar.set(null);
    }
  }
}
